import { useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { notify } from "@/lib/notify";
import { trpc } from "@/lib/trpc";

type FavoriteRow = {
  id: number;
  userId: number;
  carId: number;
  createdAt: Date;
};

export function useFavoriteToggle() {
  const { isAuthenticated } = useAuth();
  const utils = trpc.useUtils();

  const { data: favorites } = trpc.favorites.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  const mutation = trpc.favorites.toggle.useMutation({
    onMutate: async (carId) => {
      await utils.favorites.list.cancel();
      const previous = utils.favorites.list.getData();
      const exists = previous?.some((row) => row.carId === carId) ?? false;

      utils.favorites.list.setData(undefined, (current) => {
        const rows = current ?? [];
        if (exists) return rows.filter((row) => row.carId !== carId);
        const optimistic: FavoriteRow = {
          id: -Date.now(),
          userId: 0,
          carId,
          createdAt: new Date(),
        };
        return [...rows, optimistic];
      });

      return { previous };
    },
    onError: (_err, _carId, context) => {
      if (context?.previous) {
        utils.favorites.list.setData(undefined, context.previous);
      }
      notify.error("تعذّر تحديث المفضلة");
    },
    onSettled: () => {
      void utils.favorites.list.invalidate();
      void utils.favorites.listCarsEnriched.invalidate();
    },
  });

  const favoriteCarIds = useMemo(
    () => new Set(favorites?.map((row) => row.carId) ?? []),
    [favorites],
  );

  const toggleFavorite = (carId: number) => {
    if (!isAuthenticated) {
      notify.loginRequired({
        description: "سجّل الدخول لإضافة السيارات إلى المفضلة",
      });
      return;
    }
    mutation.mutate(carId);
  };

  return { favoriteCarIds, toggleFavorite, isPending: mutation.isPending };
}
